'use client';

import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import Toast from './Toast';
import { notificationPlacement, POSITIONS } from './toastMessageHelpers';
import { Position, SystemMessageProperties, ToastData } from './toastMessageTypes';

const SystemMessage: FC<SystemMessageProperties> = ({
  handleClose,
  message,
  variant,
  severity,
  title = '',
  xIcon = false,
  maxSnack = 1,
  position = 'bottomRight',
  autoHideDuration = 4000,
  sticky = false,
}) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  useEffect(() => {
    if (!message || typeof message.id !== 'number') {
      return;
    }

    const MessagePosition =
      (message.position as Position) || (position as Position) || 'bottomRight';
    const MessageIcon = typeof message.icon === 'boolean' ? (message.icon as boolean) : true;
    const MessageXIcon =
      severity === 'error' || severity === 'warning' || severity === 'success'
        ? true
        : typeof message.xIcon === 'boolean'
          ? message.xIcon
          : Boolean(xIcon);
    const MessageDuration =
      typeof message.autoHideDuration === 'number'
        ? (message.autoHideDuration as number)
        : autoHideDuration;
    const MessagePersistent =
      typeof message?.persistent === 'boolean'
        ? (message.persistent as boolean)
        : (sticky ?? false);

    const newToast: ToastData = {
      key: message.id,
      message: message.msg,
      title,
      variant,
      severity,
      xIcon: MessageXIcon,
      position: MessagePosition,
      icon: MessageIcon,
      autoHideDuration: MessageDuration,
      persistent: MessagePersistent,
    };

    setToasts((prev) => {
      const filtered = prev.filter((t) => t.key !== newToast.key);
      const updated = [...filtered, newToast];

      const byPos: Record<Position, ToastData[]> = POSITIONS.reduce(
        (accumulator, pos) => {
          accumulator[pos] = [];
          return accumulator;
        },
        {} as Record<Position, ToastData[]>,
      );

      for (const t of updated) {
        byPos[t.position].push(t);
      }

      const trimmed: ToastData[] = [];
      for (const pos of POSITIONS) {
        const stack = byPos[pos];
        if (stack.length > maxSnack) {
          trimmed.push(...stack.slice(stack.length - maxSnack));
        } else {
          trimmed.push(...stack);
        }
      }

      return trimmed;
    });
  }, [
    message,
    message?.id,
    variant,
    severity,
    title,
    xIcon,
    position,
    autoHideDuration,
    maxSnack,
    sticky,
  ]);

  const handleToastClose = useCallback(
    (id: number, reason?: string) => {
      setToasts((prev) => prev.filter((t) => t.key !== id));
      if (handleClose) {
        handleClose(undefined, reason);
      }
    },
    [handleClose],
  );

  const grouped = useMemo(() => {
    const map = new Map<Position, ToastData[]>();
    POSITIONS.forEach((p) => map.set(p, []));
    for (const t of toasts) {
      map.get(t.position)!.push(t);
    }
    return map;
  }, [toasts]);

  return (
    <>
      {POSITIONS.map((pos) => {
        const list = grouped.get(pos)!;
        if (!list.length) {
          return null;
        }

        const hasUrgent = list.some((t) => t.severity === 'error' || t.severity === 'warning');
        const live = hasUrgent ? 'assertive' : 'polite';

        return (
          <div
            key={pos}
            className={`${notificationPlacement(pos)} pointer-events-none`}
            aria-live={live}
            aria-atomic="true"
          >
            {list.map((toast) => (
              <Toast key={toast.key} data={toast} onClose={handleToastClose} />
            ))}
          </div>
        );
      })}
    </>
  );
};

export default SystemMessage;
